/**
 * @module linkage/chartTypeSwitch/reconcileForChartType/reconcileChartSwitch
 * @description 切图 L2 — chart_switch：用户已保存勾选后，顶栏切到 Table/Bar/Line 时将新图类型并入 chartTypes
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import { CHART_SWITCH_DISPLAY_ORDER } from '../../../settingGroups/ChartSwitch/constants';
import type { QBStateSnapshot } from '../../../types';
import { applyLayerPatch } from './applyLayerPatch';
import type { IReconcileLayerResult } from './reconcileTypes';

const CHART_SWITCH_ZONE = new Set<TChartType>([
  ChartType.Table,
  ChartType.Bar,
  ChartType.Line,
]);

interface IChartSwitchRaw {
  show?: boolean;
  chartTypes?: TChartType[];
}

function readEffectiveChartSwitch(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): IChartSwitchRaw | undefined {
  if ('chart_switch' in draft) {
    const fromDraft = draft.chart_switch;
    if (fromDraft && typeof fromDraft === 'object') {
      return fromDraft as IChartSwitchRaw;
    }
  }
  const fromBase = base.chart_switch;
  if (fromBase && typeof fromBase === 'object') {
    return fromBase as IChartSwitchRaw;
  }
  return undefined;
}

function normalizeChartTypes(raw: TChartType[] | undefined): TChartType[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((t) => CHART_SWITCH_ZONE.has(t));
}

/** base 中已确认过的 chart_switch（show 且 chartTypes 非空） */
function isChartSwitchCommittedInBase(
  base: Record<string, unknown>,
): boolean {
  const raw = base.chart_switch;
  if (!raw || typeof raw !== 'object') {
    return false;
  }
  const typed = raw as IChartSwitchRaw;
  return Boolean(typed.show) && normalizeChartTypes(typed.chartTypes).length > 0;
}

/**
 * chartTypes 为空（仅开开关、未手动勾选）时不改配置；
 * 已有勾选时，将目标 chartType 并入 chartTypes：
 * - 已确认在 base：同时写 base + draft（图表区读 base）
 * - 仅 draft 未确认：只写 draft
 */
export function reconcileChartSwitchForChartType(
  chartType: TChartType,
  _qb: QBStateSnapshot,
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): IReconcileLayerResult {
  if (!CHART_SWITCH_ZONE.has(chartType)) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  const raw = readEffectiveChartSwitch(base, draft);
  if (!raw?.show) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  const chartTypes = normalizeChartTypes(raw.chartTypes);
  if (chartTypes.length === 0) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  if (chartTypes.includes(chartType)) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  const typeSet = new Set<TChartType>([...chartTypes, chartType]);
  const nextChartTypes = CHART_SWITCH_DISPLAY_ORDER.filter((t) =>
    typeSet.has(t),
  );
  const nextChartSwitch = { show: true, chartTypes: nextChartTypes };
  const nextDraft = applyLayerPatch(draft, { chart_switch: nextChartSwitch });
  const draftPatch = { chart_switch: nextChartSwitch };

  if (!isChartSwitchCommittedInBase(base)) {
    return {
      base,
      draft: nextDraft,
      basePatch: {},
      draftPatch,
    };
  }

  const nextBase = applyLayerPatch(base, { chart_switch: nextChartSwitch });
  return {
    base: nextBase,
    draft: nextDraft,
    basePatch: { chart_switch: nextChartSwitch },
    draftPatch,
  };
}

